import React from 'react';
import { Card, Tag, Typography, Button, Space, Tooltip } from 'antd';
import { ShoppingCartOutlined, EyeOutlined } from '@ant-design/icons';
import { PLACEHOLDER_IMG, formatCurrency, getDietaryTagColor } from '../../utils/ui';
import { getImageWithFallback, handleImageError, getAllImages } from '../../utils/images';
import ImageCarousel from './ImageCarousel';

const { Text } = Typography;

function MenuCard({ item, onView, onAddToCart, extraTopRight }) {
	// Safety check for undefined item
	if (!item) {
		return (
			<Card className="card-ui" size="small" loading>
				<Card.Meta title="Loading..." description="Please wait..." />
			</Card>
		);
	}

	// Derive emoji context from item tags/categories
	const emojiContext = (() => {
		try {
			const name = (item?.name || '').toLowerCase();
			const tags = (item?.cuisine_tags || []).join(' ').toLowerCase();
			const all = name + ' ' + tags;
			if (/dessert|sweet|cake|pastry|laddu|rasg|gulab/.test(all)) return 'dessert';
			if (/juice|shake|drink|beverage|coffee|tea|soda/.test(all)) return 'beverage';
			if (/fish|prawn|shrimp|sea ?food|crab|lobster|squid/.test(all)) return 'seafood';
			if (/noodle|pasta|spaghetti|ramen|soba|udon/.test(all)) return 'noodles';
			if (/rice|biryani|pulao|khichdi/.test(all)) return 'rice';
			if (/snack|chaat|fries|sandwich|pizza|burger|wrap|taco|roll/.test(all)) return 'snacks';
			if (item?.is_vegan) return 'vegan';
			if (item?.is_vegetarian) return 'vegetarian';
			if (item?.is_spicy) return 'spicy';
			return undefined;
		} catch(_) { return undefined; }
	})();

	const allImages = getAllImages(item, 'medium');
	const hasMultipleImages = allImages.length > 1;

	return (
		<Card 
			className="card-ui hover:shadow-lg transition-all duration-300" 
			size="small"
			bodyStyle={{ padding: '12px' }}
			data-emoji-context={emojiContext}
			cover={
				<div className="relative flex items-center justify-center bg-gray-50" style={{ height: '128px' }}>
					{hasMultipleImages ? (
						<ImageCarousel
							item={item}
							size="medium"
							height={128}
							showIndicators={true}
							showArrows={false}
							showPreview={true}
							autoplay={true}
							maxImages={2}
						/>
					) : (
						<img
							alt={item.name || 'Menu item'}
							src={getImageWithFallback(item, 'medium')}
							className="max-w-full max-h-full w-auto h-auto object-contain"
							onError={handleImageError}
						/>
					)}
					{extraTopRight && (
						<div className="absolute top-2 right-2">{extraTopRight}</div>
					)}
				</div>
			}
			actions={[
				<Button key="view" size="small" icon={<EyeOutlined />} onClick={() => onView?.(item)}>View</Button>,
				<Button key="add" type="primary" size="small" icon={<ShoppingCartOutlined />} onClick={() => onAddToCart?.(item)}>Add</Button>
			]}
		>
			<Card.Meta
				title={
					<div className="flex items-center justify-start">
						<Text strong className="text-base">{item.name}</Text>
					</div>
				}
				description={
					<div className="space-y-2">
						<Text type="secondary">{item.vendor?.business_name}</Text>
						<div className="flex flex-wrap gap-1">
							{item.cuisine_tags?.slice(0, 3).map((tag, index) => (
								<Tag key={index} size="small" color={getDietaryTagColor(tag)}>{tag}</Tag>
							))}
						</div>
					</div>
				}
			/>
		</Card>
	);
}

export default MenuCard;
